import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixEcomWishlistV1WishlistsUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    _: [
      {
        srcPath: '/_api/wishlist',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/wishlist',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/wishlist',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/wishlist',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/ecom/v1/wishlists',
        destPath: '/v1/wishlists',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_ecom_wishlists';

/** Returns the caller's default wishlist. Empty representation if none exists. */
export function getDefaultWishlist(
  payload: object
): RequestOptionsFactory<any> {
  function __getDefaultWishlist({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.wishlist',
      method: 'GET' as any,
      methodFqn: 'wix.ecom.wishlist.v1.Wishlists.GetDefaultWishlist',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomWishlistV1WishlistsUrl({
        protoPath: '/v1/wishlists/get-default',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'wishlist.createdDate' },
              { path: 'wishlist.updatedDate' },
              { path: 'wishlist.items.addedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getDefaultWishlist;
}

/**
 * Adds items to the caller's wishlist (auto-created on first use). Duplicates are skipped;
 * exceeding the 100-item cap returns WISHLIST_ITEMS_LIMIT_EXCEEDED.
 */
export function addItem(payload: object): RequestOptionsFactory<any> {
  function __addItem({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.wishlist',
      method: 'POST' as any,
      methodFqn: 'wix.ecom.wishlist.v1.Wishlists.AddItem',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomWishlistV1WishlistsUrl({
        protoPath: '/v1/wishlists/add-item',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'wishlist.createdDate' },
              { path: 'wishlist.updatedDate' },
              { path: 'wishlist.items.addedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __addItem;
}

/** Removes an item from the caller's wishlist. Absent item / no wishlist is a silent no-op. */
export function removeItem(payload: object): RequestOptionsFactory<any> {
  function __removeItem({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.wishlist',
      method: 'POST' as any,
      methodFqn: 'wix.ecom.wishlist.v1.Wishlists.RemoveItem',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomWishlistV1WishlistsUrl({
        protoPath: '/v1/wishlists/remove-item',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'wishlist.createdDate' },
              { path: 'wishlist.updatedDate' },
              { path: 'wishlist.items.addedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __removeItem;
}

/** Synchronously assigns/unassigns tags on wishlists by ID. */
export function bulkUpdateWishlistTags(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateWishlistTags({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.wishlist',
      method: 'POST' as any,
      methodFqn: 'wix.ecom.wishlist.v1.Wishlists.BulkUpdateWishlistTags',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomWishlistV1WishlistsUrl({
        protoPath: '/v1/bulk/wishlists/update-tags',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateWishlistTags;
}

/**
 * Asynchronously assigns/unassigns tags on wishlists matching a filter
 * (empty filter = all wishlists belonging to the site_member_id). Returns a job_id.
 */
export function bulkUpdateWishlistTagsByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateWishlistTagsByFilter({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.wishlist',
      method: 'POST' as any,
      methodFqn:
        'wix.ecom.wishlist.v1.Wishlists.BulkUpdateWishlistTagsByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomWishlistV1WishlistsUrl({
        protoPath: '/v1/bulk/wishlists/update-tags-by-filter',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateWishlistTagsByFilter;
}
